<script>
    import Button from './Button.svelte';
    import Card from './Card.svelte';
    import H2 from './H2.svelte';
    import Prose from './Prose.svelte';
    import PurchasePaddle from './PurchasePaddle.svelte';
    import { format_age_range } from './utils/format_ages';
    import { img_cdn, img_cdn_srcset } from './utils/image';
    import { logEvent } from './utils/logs';

    /** @type {Worksheet} */
    export let worksheet;
    export let feature = 'worksheets-list-xlcard';
    export let h_level = 2;
    /** @type {string | undefined} */
    export let title = undefined;
    /** @type {string | undefined} */
    export let lead = undefined;

    let className = '';
    export { className as class };

    $: age_label = format_age_range(worksheet.min_age, worksheet.max_age);

    $: eyebrow_labels = [age_label, ...(worksheet.content_labels || [])].filter(
        Boolean
    );

    $: worksheet_url = `/worksheets/${worksheet.slug}`;

    /** @type {ImageTransformOptions} */
    const image_options = { w: 309, h: 304, fit: 'contain' };

    function track_download_click() {
        logEvent(`worksheets-download-click-${feature}`, { slug: worksheet.slug });
    }
</script>

<Card
    class="relative @container @lg:min-h-80 {className}"
    href={worksheet_url}
    click_event={{
        name: `worksheets-card-click-${feature}`,
        data: { slug: worksheet.slug }
    }}
    aria_label={worksheet.title}
>
    <div
        class="relative z-10 flex flex-col gap-6 h-full pb-40 @lg:pb-0 pointer-events-none"
    >
        <div class="flex flex-wrap gap-2 pr-16">
            {#each eyebrow_labels as label}
                <span
                    class="text-sm bg-surface-highlight rounded-full px-3 py-1 text-nowrap"
                >
                    {label}
                </span>
            {/each}
        </div>

        <div class="space-y-2 @lg:mt-auto">
            <H2 tag={`h${h_level}`} class="link link-el @lg:pr-[39%]">
                {title || worksheet.title}
            </H2>

            <Prose lg class="prose-p:text-pretty max-w-none @lg:pr-[48%]">
                {@html lead || worksheet.lead}
            </Prose>

            {#if worksheet.purchased}
                <div class="@lg:pr-[48%] relative pointer-events-auto">
                    <Button
                        primary
                        outline
                        route={worksheet_url}
                        on:click={track_download_click}
                    >
                        Download
                    </Button>
                </div>
            {:else}
                <div
                    class="flex flex-col items-start gap-3  @lg:flex-row @lg:items-center @lg:gap-4 @lg:flex-wrap @lg:pr-[48%] @3xl:gap-6 pointer-events-auto"
                >
                    <PurchasePaddle
                        product={worksheet}
                        style="small"
                        cta="Buy Now"
                        cta_free="Get for Free"
                        {feature}
                    >
                        <svelte:fragment
                            slot="after"
                            let:total_discount_percent
                            let:is_free
                        >
                            {#if total_discount_percent > 0}
                                <span
                                    class="
                                        text-lg font-bold bg-purple-600
                                        rounded-l-full
                                        py-1 pl-3 pr-1
                                        absolute
                                        -top-0.5 -right-4 md:-right-6 z-20"
                                >
                                    {#if is_free}
                                        It's a gift!
                                    {:else}
                                        &minus;{total_discount_percent}%
                                    {/if}
                                </span>
                            {/if}
                        </svelte:fragment>
                    </PurchasePaddle>
                </div>
            {/if}
        </div>
    </div>

    {#if worksheet.big_side_image}
        <img
            src={img_cdn(worksheet.big_side_image, image_options)}
            srcset={img_cdn_srcset(worksheet.big_side_image, image_options)}
            alt={worksheet.title}
            width="309"
            height="304"
            loading="lazy"
            class="absolute bottom-0 right-0 z-0 w-56 @lg:w-[42%] @xl:w-[45%] max-h-full object-contain object-bottom-right pointer-events-none"
        />
    {/if}
</Card>
